/**
 * Camera bounds and zoom limits for the battle arena.
 *
 * The HUD, the zoom controls and the action buttons are drawn over the edges of
 * the arena, so a Garaimon standing in the corner can end up underneath them.
 * Bounds that stopped exactly at the arena edge would give the player no way to
 * bring that corner into the open, because there would be no slack to scroll
 * into. The padding below is that slack.
 *
 * These bounds are advisory only. Phaser's own camera clamp cannot centre the
 * view once the zoomed-out arena becomes smaller than the viewport: it pins the
 * camera to the edge of the bounds, so zooming out would push the arena into one
 * corner and put all the padding opposite it, clipping the far edge of the field.
 * The battle controls therefore turn `useBounds` off and clamp the camera
 * themselves, centring the view when it is larger than the bounds. The bounds are
 * shared from here so the scene and the controls cannot disagree.
 *
 * This module deliberately has no Phaser and no DOM dependency, so `main.ts` can
 * import from it without pulling Phaser into the initial page bundle.
 */

/** Zoomed out far enough to see the whole arena and its edges. */
export const MIN_ZOOM = 0.7;

/** Zoomed in far enough to watch a single exchange closely. */
export const MAX_ZOOM = 2.5;

/**
 * How far past the arena edge the camera may travel. At the default zoom this is
 * the distance a player can drag the arena to pull a corner out from under the
 * HUD; it also keeps the arena from being dragged away entirely.
 */
export const ARENA_CAMERA_PADDING = 80;

export interface CameraBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The scrollable bounds for an arena of the given world size. */
export function arenaCameraBounds(worldWidth: number, worldHeight: number): CameraBounds {
  return {
    x: -ARENA_CAMERA_PADDING,
    y: -ARENA_CAMERA_PADDING,
    width: worldWidth + ARENA_CAMERA_PADDING * 2,
    height: worldHeight + ARENA_CAMERA_PADDING * 2,
  };
}
